import React, { useEffect, useState } from 'react';
import { Modal, Typography, Table, Tag, Statistic, Row, Col, Divider, Button, message, Space, InputNumber, Radio } from 'antd';
import { FilePdfOutlined, CheckCircleOutlined, WalletOutlined, EuroOutlined, PrinterOutlined } from '@ant-design/icons';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import InvoicePrintModal from '../components/InvoicePrintModal';

const { Title, Text } = Typography;

const InvoiceDetails = ({ visible, invoiceId, onCancel, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [printVisible, setPrintVisible] = useState(false);

    // Payment State
    const [payMode, setPayMode] = useState(false); // boolean to toggle payment view
    const [paymentAmount, setPaymentAmount] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState(4); // 4=Cash

    useEffect(() => {
        if (visible && invoiceId) {
            fetchDetails();
            setPayMode(false);
        }
    }, [visible, invoiceId]);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/invoices/${invoiceId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDetails(res.data);
            setPaymentAmount(parseFloat(res.data.remaintopay));
        } catch (error) {
            console.error(error);
            message.error('Failed to load invoice details');
        } finally {
            setLoading(false);
        }
    };

    const handleValidate = async () => {
        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/invoices/${invoiceId}/validate`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            message.success('Invoice Validated');
            fetchDetails();
            onSuccess(); // To refresh list in parent
        } catch (error) {
            console.error(error);
            message.error('Validation failed');
        } finally {
            setActionLoading(false);
        }
    };

    const handlePayment = async () => {
        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/invoices/${invoiceId}/payments`, {
                amount: paymentAmount,
                payment_mode_id: paymentMethod
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            message.success('Payment recorded');
            setPayMode(false);
            fetchDetails();
            onSuccess();
        } catch (error) {
            console.error(error);
            message.error('Payment failed');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownload = async (isPrint = false) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/invoices/${invoiceId}/document`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));

            if (isPrint) {
                window.open(url, '_blank');
            } else {
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${details.ref}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            message.error('Failed to generate PDF. The document might still be processing.');
        }
    };

    const columns = [
        { title: 'Item', dataIndex: 'libelle', key: 'libelle', render: (t, r) => t || r.description },
        { title: 'Qty', dataIndex: 'qty', key: 'qty' },
        { title: 'U.P.', dataIndex: 'subprice', key: 'subprice', render: val => formatCurrency(val) },
        { title: 'VAT %', dataIndex: 'tva_tx', key: 'tva_tx', render: val => `${parseFloat(val)}%` },
        { title: 'Total', dataIndex: 'total_ttc', key: 'total_ttc', render: val => formatCurrency(val) }
    ];

    if (!details) return null;

    const remToPay = parseFloat(details.remaintopay || 0);
    const isDraft = details.status === '0' || details.statut === '0';
    const isUnpaid = (details.status === '1' || details.statut === '1') && remToPay > 0.001;
    const isPaid = (details.status === '2' || details.statut === '2') || ((details.status === '1' || details.statut === '1') && remToPay <= 0.001);

    return (
        <Modal
            title={
                <Space>
                    <Text>Invoice {details.ref}</Text>
                    {isDraft && <Tag>Draft</Tag>}
                    {isPaid && <Tag color="success">Paid</Tag>}
                    {isUnpaid && <Tag color="warning">Unpaid</Tag>}
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={800}
            destroyOnClose
        >
            <div style={{ marginBottom: 20 }}>
                <Row gutter={16}>
                    <Col span={8}>
                        <Statistic title="Total (TTC)" value={details.total_ttc} precision={3} suffix="JOD" />
                    </Col>
                    <Col span={8}>
                        <Statistic
                            title="Remaining to Pay"
                            value={details.remaintopay}
                            precision={3}
                            suffix="JOD"
                            valueStyle={{ color: remToPay <= 0.001 ? '#52c41a' : '#cf1322' }}
                        />
                    </Col>
                    <Col span={8} style={{ textAlign: 'right' }}>
                        <Space direction="vertical">
                            <Text type="secondary">Date: {new Date(details.date * 1000).toLocaleDateString()}</Text>
                            {/* Actions */}
                            <Space>
                                {isDraft && (
                                    <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleValidate} loading={actionLoading}>
                                        Validate
                                    </Button>
                                )}
                                {isUnpaid && !payMode && (
                                    <Button type="primary" icon={<WalletOutlined />} onClick={() => setPayMode(true)}>
                                        Add Payment
                                    </Button>
                                )}
                                {!isDraft && (
                                    <>
                                        <Button icon={<PrinterOutlined />} onClick={() => setPrintVisible(true)}>Print</Button>
                                        <Button icon={<FilePdfOutlined />} onClick={() => handleDownload()}>Download</Button>
                                    </>
                                )}
                            </Space>
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* Payment Section */}
            {payMode && (
                <div style={{ background: '#f6ffed', padding: 16, borderRadius: 8, marginBottom: 16, border: '1px solid #b7eb8f' }}>
                    <Title level={5}>Record Payment</Title>
                    <Space size="large" align="end">
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Text strong>Amount</Text>
                            <InputNumber
                                style={{ width: 150 }}
                                value={paymentAmount}
                                max={parseFloat(details.remaintopay)}
                                min={0}
                                onChange={setPaymentAmount}
                                precision={3}
                                addonAfter="JOD"
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Text strong>Method</Text>
                            <Radio.Group value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} buttonStyle="solid">
                                <Radio.Button value={4}>Cash</Radio.Button>
                                <Radio.Button value={6}>CliQ</Radio.Button>
                            </Radio.Group>
                        </div>
                        <Button type="primary" onClick={handlePayment} loading={actionLoading}>Confirm</Button>
                        <Button onClick={() => setPayMode(false)}>Cancel</Button>
                    </Space>
                </div>
            )}

            <Table
                dataSource={details.lines}
                columns={columns}
                rowKey="id"
                pagination={false}
                size="small"
                loading={loading}
                bordered
            />

            <InvoicePrintModal
                visible={printVisible}
                invoiceId={invoiceId}
                onCancel={() => setPrintVisible(false)}
                forceA4={true}
            />
        </Modal>
    );
};

export default InvoiceDetails;
